import {
  decryptWithCipher,
  decryptWithSpaceKey,
  deserializeCipherPayload,
  encryptWithSpaceKey,
  fromBytes,
  serializeCipherPayload,
  toBytes,
  type CommunityCipher,
  type SpacePayloadAad,
} from '@adieuu/crypto';
import type { PublicSpaceMessage } from '@adieuu/shared';
import { peekSpaceKey } from '../../services/spaceCipherService';
import { verifySpaceMessage } from './spaceMessageSignature';

export interface CipherFields {
  ciphertext: string;
  nonce: string;
  cipherId: string;
}

export interface DecryptableMessage {
  content?: string;
  ciphertext?: string;
  nonce?: string;
  cipherId?: string;
  signature?: string;
  fromIdentityId?: string;
  clientMessageId?: string;
  spaceId?: string;
  channelId?: string;
}

export interface EncryptContentOptions {
  spaceKey: Uint8Array;
  aad: SpacePayloadAad;
}

export interface DecryptBodyOptions {
  spaceKey?: Uint8Array;
  aad?: SpacePayloadAad | readonly SpacePayloadAad[];
  /** When true, never return plaintext `content` (E2EE channels). */
  requireCipher?: boolean;
  allowLegacyUntagged?: boolean;
}

export interface DecryptBodyDetailed {
  text: string;
  usedLegacyUntagged: boolean;
}

export interface SpaceMessageTrust {
  body: string;
  signatureVerified?: boolean;
  decryptionError?: string;
}

export function looksLikeCipherPayload(content: string): boolean {
  try {
    const parsed = JSON.parse(content);
    return !!(parsed && parsed.ciphertext && parsed.nonce && parsed.cipherId);
  } catch {
    return false;
  }
}

function firstAad(
  aad: SpacePayloadAad | readonly SpacePayloadAad[] | undefined,
): SpacePayloadAad | undefined {
  if (!aad) return undefined;
  if (Array.isArray(aad)) return aad[0];
  return aad as SpacePayloadAad;
}

function resolveSpaceKey(
  cipher: CommunityCipher,
  opts: DecryptBodyOptions | undefined,
): Uint8Array | null {
  if (opts?.spaceKey) return opts.spaceKey;
  const ctx = firstAad(opts?.aad);
  if (!ctx) return null;
  return peekSpaceKey(cipher, ctx.spaceId);
}

/**
 * Decrypt a message body from dedicated cipher fields or plaintext content.
 * Falls back to `fallback` when cipher fields are present but decryption fails.
 *
 * E2EE channels must pass `{ requireCipher: true }` so a compromised server
 * cannot inject plaintext `content` that the client would render.
 */
export function decryptBody(
  msg: DecryptableMessage | undefined,
  cipher: CommunityCipher | null | undefined,
  fallback: string,
  opts?: DecryptBodyOptions,
): string {
  return decryptBodyDetailed(msg, cipher, fallback, opts).text;
}

export function decryptBodyDetailed(
  msg: DecryptableMessage | undefined,
  cipher: CommunityCipher | null | undefined,
  fallback: string,
  opts?: DecryptBodyOptions,
): DecryptBodyDetailed {
  if (!msg) return { text: '', usedLegacyUntagged: false };

  if (msg.ciphertext && msg.nonce && msg.cipherId) {
    if (!cipher) return { text: fallback, usedLegacyUntagged: false };
    try {
      const payload = deserializeCipherPayload({
        ciphertext: msg.ciphertext,
        nonce: msg.nonce,
        cipherId: msg.cipherId,
      });
      const spaceKey = resolveSpaceKey(cipher, opts);
      if (spaceKey && opts?.aad) {
        const result = decryptWithSpaceKey(cipher, spaceKey, payload, opts.aad, {
          allowLegacyUntagged: opts.allowLegacyUntagged !== false,
        });
        return {
          text: fromBytes(result.plaintext),
          usedLegacyUntagged: result.usedLegacyUntagged,
        };
      }
      // Space key not cached yet: still open historical untagged payloads.
      if (opts?.allowLegacyUntagged !== false) {
        return {
          text: fromBytes(decryptWithCipher(cipher, payload)),
          usedLegacyUntagged: true,
        };
      }
      return { text: fallback, usedLegacyUntagged: false };
    } catch {
      return { text: fallback, usedLegacyUntagged: false };
    }
  }

  if (opts?.requireCipher) {
    return { text: fallback, usedLegacyUntagged: false };
  }

  const content = msg.content;
  if (!content) return { text: '', usedLegacyUntagged: false };
  if (looksLikeCipherPayload(content)) return { text: fallback, usedLegacyUntagged: false };
  return { text: content, usedLegacyUntagged: false };
}

/**
 * Encrypt plaintext into structured cipher fields for transport.
 * New ciphertext is sealed with the Argon2 space key and context AAD.
 */
export function encryptContent(
  cipher: CommunityCipher,
  plaintext: string,
  opts: EncryptContentOptions,
): CipherFields {
  const encrypted = encryptWithSpaceKey(cipher, opts.spaceKey, toBytes(plaintext), opts.aad);
  const serialized = serializeCipherPayload(encrypted);
  return {
    ciphertext: serialized.ciphertext,
    nonce: serialized.nonce,
    cipherId: serialized.cipherId,
  };
}

/**
 * Decrypt a single edit-history revision entry. Returns `{ plaintext }` on
 * success or `{ decryptionError }` if the content cannot be decrypted.
 */
export function decryptEditHistoryEntry(
  entry: DecryptableMessage,
  cipher: CommunityCipher,
  opts?: DecryptBodyOptions,
): { plaintext: string } | { decryptionError: string } {
  const detailed = decryptBodyDetailed(entry, cipher, '', {
    ...opts,
    requireCipher: opts?.requireCipher ?? !!(entry.ciphertext && entry.nonce && entry.cipherId),
  });
  if (entry.ciphertext && entry.nonce && entry.cipherId) {
    if (!detailed.text) return { decryptionError: 'Unable to decrypt' };
    return { plaintext: detailed.text };
  }
  if (opts?.requireCipher) {
    return { decryptionError: 'Unable to decrypt' };
  }
  if (entry.content !== undefined) {
    if (looksLikeCipherPayload(entry.content)) {
      return { decryptionError: 'Unable to decrypt' };
    }
    return { plaintext: entry.content };
  }
  return { decryptionError: 'Unable to decrypt' };
}

/**
 * Decrypt + signature policy for an E2EE Space message.
 *
 * - Missing cipher fields → placeholder (`requireCipher`).
 * - Unsigned legacy (untagged) ciphertext → show body, `signatureVerified` omitted.
 * - AAD-tagged ciphertext without a signature → hide (treat as failed).
 * - Signature present but public key missing → placeholder until keys load.
 * - Invalid signature → hide body (`decryptionError`).
 */
export function trustSpaceMessageBody(
  msg: PublicSpaceMessage,
  cipher: CommunityCipher | null | undefined,
  fallback: string,
  opts: DecryptBodyOptions & {
    signingPublicKey?: string;
    pqSigningPublicKey?: string;
    requireCipher: boolean;
  },
): SpaceMessageTrust {
  const detailed = decryptBodyDetailed(msg, cipher, fallback, opts);
  if (!opts.requireCipher) {
    return { body: detailed.text };
  }

  const hasCipherFields = !!(msg.ciphertext && msg.nonce && msg.cipherId);
  if (!hasCipherFields) {
    return { body: fallback };
  }

  if (!msg.signature) {
    if (detailed.usedLegacyUntagged && detailed.text && detailed.text !== fallback) {
      return { body: detailed.text };
    }
    return { body: '', decryptionError: fallback };
  }

  if (!opts.signingPublicKey) {
    return { body: fallback };
  }

  if (!msg.spaceId || !msg.channelId || !msg.fromIdentityId || !msg.clientMessageId) {
    return { body: '', signatureVerified: false, decryptionError: fallback };
  }

  const verified = verifySpaceMessage(
    opts.signingPublicKey,
    {
      spaceId: msg.spaceId,
      channelId: msg.channelId,
      fromIdentityId: msg.fromIdentityId,
      clientMessageId: msg.clientMessageId,
    },
    msg.ciphertext!,
    msg.nonce!,
    msg.cipherId!,
    msg.signature,
    opts.pqSigningPublicKey,
  );
  if (!verified) {
    return { body: '', signatureVerified: false, decryptionError: fallback };
  }
  if (!detailed.text || detailed.text === fallback) {
    return { body: fallback, signatureVerified: true };
  }
  return { body: detailed.text, signatureVerified: true };
}
